import * as amplitude from "@amplitude/analytics-browser";
import { defineRoute, onLocationChange } from "@stencil-mini-router";
import { userAgentEnrichmentPlugin } from "@amplitude/plugin-user-agent-enrichment-browser";
import { isEnv, Environment } from "@app/util/defs";

/**
 * Takes a path to match on and either a string which represents the amplitude event name
 * or a callback which takes the strongly typed path params for the route returns an object
 * with the event name and attributes to track. If the callback returns undefined, no event
 * will be tracked.
 *
 * Returns a function which when called will return true if the current path matches the
 * passed path and will track the event if it does using either the passed string as the
 * event name or the return value of the callback as the arguments to the track call.
 *
 * example:
 *
 * ```
 * trackedRoute("/products/:id", (matchedPath) => ({
 *  name: "Product Viewed",
 *  attrs: { "Product Id": matchedPath.params.id }
 * }))
 * ```
 */
function trackedRoute<
  Path extends string,
  RouteDef extends ReturnType<typeof defineRoute<Path>>,
  RouteDefCallback extends Parameters<RouteDef["match"]>[0],
  RouteDefCallbackArgs extends Parameters<RouteDefCallback>,
>(
  path: Path,
  nameOrCallback:
    | string
    | ((
        matchedPath: RouteDefCallbackArgs[0],
        amplitudeAttrs: AmplitudeInitArgs,
      ) => {
        name: string;
        attrs: Record<string, any>;
      } | void),
): (amplitudeAttrs: AmplitudeInitArgs) => boolean | undefined {
  return (amplitudeAttrs) => {
    const route = defineRoute(path, true);
    return route.match((matchedPath) => {
      if (nameOrCallback instanceof Function) {
        const trackArgs = nameOrCallback(matchedPath, amplitudeAttrs);
        if (trackArgs) track(trackArgs.name, trackArgs.attrs);
      } else {
        track(nameOrCallback);
      }

      return true;
    });
  };
}

function routeMatcher(path: string, callback = () => true) {
  return () => {
    const route = defineRoute(path, true);
    return route.match(callback);
  };
}

const routes = [
  // sign in, sign up
  trackedRoute("/signup/:planSlug", (matchedPath) => ({
    name: "Sign Up Viewed",
    attrs: { Plan: planFromSlug(matchedPath.params.planSlug) },
  })),
  trackedRoute("/login", (r) => {
    if (r.queryParams.get("logged_out") === "true") {
      amplitude?.reset();
    }

    return { name: "Sign In Viewed", attrs: {} };
  }),

  // onboarding survey
  trackedRoute("/onboarding-survey", "Onboarding Survey Viewed"),

  // account settings
  trackedRoute("/account", "Account Summary Viewed"),
  trackedRoute("/account/profile", "Profile Viewed"),
  trackedRoute("/account/change-password", "Change Password Viewed"),
  trackedRoute("/account/phone-number", "Change Phone Number Viewed"),
  trackedRoute("/email-trouble", "Email Trouble Viewed"),
  trackedRoute("/change-email", "Change Email Viewed"),
  trackedRoute("/verify-contact-email", (_, amplitudeAttrs) => {
    let attrs: { Plan?: ReturnType<typeof planFromSlug> } = {};

    if (amplitudeAttrs.user) {
      attrs["Plan"] = planFromSlug(amplitudeAttrs.user.planName);
    }

    return {
      name: "Email Verification Viewed",
      attrs: attrs,
    };
  }),
  trackedRoute("/forgot-password", "Forgot Password Viewed"),
  trackedRoute("/goodbye", "Shop Closed Confirmation Viewed"),

  // shop preferences
  trackedRoute("/account/maintenance", "Maintenance Mode Settings Viewed"),
  trackedRoute("/account/your-shop", "Shop Info Viewed"),
  trackedRoute("/account/your-shop/domain", "Custom Domain Settings Viewed"),
  trackedRoute("/account/location", "Location and Currency Settings Viewed"),
  trackedRoute("/account/payment-options", "Payment Options Settings Viewed"),
  trackedRoute("/account/preferences", "Preferences Settings Viewed"),
  trackedRoute(
    "/account/shipping-profiles", () => ({
      name: "Shipping Settings Viewed",
      attrs: {"Shipping Method": "Shipping Profiles"}
    })
  ),
  trackedRoute("/account/automatic_shipping_rates", () => ({
    name: "Shipping Settings Viewed",
    attrs: {"Shipping Method": "Automatic Shipping Rates"}
  })),
  trackedRoute(
    "/account/shipping-profiles/rates/new",
    "Add New Default Shipping Profile Viewed",
  ),
  trackedRoute("/account/taxes", "Shop Tax Rate Settings Viewed"),
  trackedRoute("/account/taxes/new", "Add Shop Tax Rate Viewed"),
  trackedRoute("/account/taxes/:id/edit", "Edit Shop Tax Rate Viewed"),
  trackedRoute("/account/your-shop/domain/edit", "Add Custom Domain Viewed"),

  // maintenance mode and shop launch
  trackedRoute("/account/share-your-store", "Share Your Store Viewed"),
  trackedRoute("/dashboard/launched", "Congrats Shop Launched Viewed"),
  trackedRoute("/account/maintenance", "Account Maintenance Viewed"),

  // billing
  trackedRoute("/billing", "Billing Summary Viewed"),
  trackedRoute("/billing/close-shop", "Confirm Close Shop Viewed"),
  trackedRoute("/billing/close-shop/survey", "Cancellation Survey Viewed"),
  trackedRoute("/billing/edit", "Edit Billing Viewed"),
  trackedRoute("/billing/locked", "Locked Billing Viewed"),
  trackedRoute("/billing/downgrade/:plan", (matchedPath) => ({
    name: "Billing Downgrade Plan Viewed",
    attrs: { Plan: planFromSlug(matchedPath.params.plan) },
  })),
  trackedRoute("/billing/upgrade/free-to-paid/:plan", (matchedPath) => ({
    name: "Billing Upgrade Plan Viewed",
    attrs: { Plan: planFromSlug(matchedPath.params.plan) },
  })),
  trackedRoute("/billing/upgrade/:plan", (matchedPath) => ({
    name: "Billing Upgrade Plan Viewed",
    attrs: { Plan: planFromSlug(matchedPath.params.plan) },
  })),
  trackedRoute("/billing/plan", "Plans Page Viewed"),
  trackedRoute("/billing/plan/downgrade/:id/confirm", (matchedPath) => ({
    name: "Downgrade What You'll Lose Viewed",
    attrs: { "Downgrade Plan Id": matchedPath.params.id },
  })),
  trackedRoute("/billing/plan/downgrade/survey", (matchedPath) => ({
    name: "Downgrade Survey Viewed",
    attrs: { "Downgrade Plan Id": matchedPath.params.id },
  })),
  trackedRoute("/billing/pause/new", "Pause Shop Viewed"),
  trackedRoute("/billing/pause/confirmation", (r) => ({
    name: "Pause Shop Confirmation Viewed",
    attrs: { "Duration": r.queryParams.get("pause_month_count") }
  })),
  trackedRoute("/billing/pause", "Pause Lock Viewed"),

  // admin: general
  trackedRoute("/dashboard", () => ({
    name: "Admin Dashboard Viewed",
    attrs: { "Admin Version": "Legacy" }
  })),
  trackedRoute("/orders", (r) => {
    const filter =
      r.queryParams.get("shipped") === "true" ? "Shipped" : "Unshipped";
    return { name: "Orders Summary Viewed", attrs: { Filter: filter } };
  }),
  trackedRoute("/orders/:id", () => {
    const order = (window as any).bc?.order || {};

    return {
      name: "Order Detail Viewed",
      attrs: getOrderDetails(order),
    };
  }),
  trackedRoute("/orders/:id/edit", (matchedPath) => ({
    name: "Edit Customer Order Info Viewed",
    attrs: { "Order Number": matchedPath.params.id },
  })),
  trackedRoute("/orders/:orderId/shipments/:shipId/edit", (matchedPath) => ({
    name: "Edit Order Shipment Viewed",
    attrs: { "Order Number": matchedPath.params.orderId },
  })),
  trackedRoute("/products", "Products Summary Viewed"),
  trackedRoute("/products/table", "Products Table Viewed"),
  trackedRoute("/products/new", "Add Product Viewed"),
  trackedRoute("/products/:id/edit", (matchedPath) => ({
    name: "Edit Product Viewed",
    attrs: { "Product Id": matchedPath.params.id },
  })),
  trackedRoute("/products/import/confirmation", "Bulk Product Import Summary Viewed"),

  trackedRoute("/discounts", (r) => {
    const filter =
      r.queryParams.get("expired") === "true" ? "Expired" : "Active";
    return { name: "Discounts Summary Viewed", attrs: { Filter: filter } };
  }),
  trackedRoute("/discounts/new", "Add Discount Viewed"),
  trackedRoute("/discounts/:id/edit", (matchedPath) => ({
    name: "Edit Discount Viewed",
    attrs: { "Discount Id": matchedPath.params.id },
  })),

  // custo
  trackedRoute("/account/design", "Custo Settings Viewed"),
  trackedRoute("/account/design/themes", "Custo Themes Viewed"),
  trackedRoute("/account/design/pages", "Custo Pages Viewed"),
  trackedRoute("/account/design/pages/new", "Custo Add New Page Viewed"),
  trackedRoute("/account/design/pages/edit/:page", (matchedPath) => ({
    name: "Custo Edit Page Viewed",
    attrs: { "Page Name": matchedPath.params.page },
  })),
  trackedRoute("/account/design/code", "Custo Code Viewed"),
  trackedRoute("/account/design/code/edit/:type", (matchedPath) => ({
    name: "Custo Code Edit Viewed",
    attrs: { "Code Type": matchedPath.params.type },
  })),
  trackedRoute("/account/design/settings/shop_sprinkles", "Custo Shop Sprinkles Viewed"),

  // integrations
  trackedRoute("/apps", "App Store Homepage Viewed"),
  trackedRoute("/apps/:permalink([a-zA-Z0-9-]+)", (r) => {
    const client_info = (window as any).bc?.oauth_client || {};

    return {
      name: "App Store App Page Viewed",
      attrs: getIntegrationsRouteAttributes(r, client_info),
    };
  }),
  trackedRoute("/oauth/unauthorized", (r) => {
    const client_info = (window as any).bc?.oauth_client || {};

    return {
      name: "Upgrade for Premium Integration Viewed",
      attrs: getIntegrationsRouteAttributes(r, client_info),
    };
  }),
  trackedRoute("/oauth/authorize", (r) => {
    const client_info = (window as any).bc?.oauth_client || {};

    return {
      name: "Connect Integration Viewed",
      attrs: getIntegrationsRouteAttributes(r, client_info),
    };
  }),
];

// Untracked routes to not get logged in Amplitude
const untrackedRoutes = [
  routeMatcher("/logout", () => {
    return true;
  }),
];

function capitalize(v: string) {
  return v[0].toUpperCase() + v.slice(1);
}

// useful attributes for analytics when looking at oauth integrations pageview events
function getIntegrationsRouteAttributes(r: any, client_info: any) {
  const client_id = client_info.identifier || "";
  const client_name = client_info.name || "";
  const client_is_premium = client_info.is_premium || false;
  const client_is_featured = client_info.is_admin_featured || false;
  const redirect_uri = r.queryParams.get("redirect_uri")
    ? decodeURIComponent(r.queryParams.get("redirect_uri"))
    : null;
  const redirect_domain = redirect_uri ? new URL(redirect_uri).hostname : null;

  const attributes: Record<string, any> = {
    "Client Id": client_id,
    "Integration Name": client_name,
    "Is Premium Integration": client_is_premium,
    "Is Admin Featured": client_is_featured,
  };

  if (redirect_uri) {
    attributes["Redirect Uri"] = redirect_uri;
    attributes["Redirect Domain"] = redirect_domain;
  }

  return attributes;
}

function getOrderDetails(order: any) {
  return {
    "Order Number": order.number,
    "Order Id": order.id,
    "Order Shipping Status": order.shipping_status,
    "Order Quantity": order.item_count,
    "Order Shipping City": order.shipping_city,
    "Order Shipping State": order.shipping_state,
    "Order Shipping Country Name": order.shipping_country_name,
    "Order Discount": order.total_discount,
    "Order Tax": order.tax,
    "Order Shipping": order.total_shipping,
    "Order Sub-Total": order.item_total,
    "Order Total": order.total_price,
  };
}

// based on code https://github.com/amplitude/Amplitude-TypeScript/blob/main/packages/marketing-analytics-browser/src/browser-client.ts#L11
//
// To get the customizations we want I couldn't use the marketing analytics browser client directly because it doesn't allow passing
// a defaultTracking option.
export type AmplitudeInitArgs = {
  amplitudeApiKey?: string;
  amplitudeUserId?: string;
  user?: IdentifyUserArgs;
};

type IdentifyUserArgs = {
  planName: string;
  createdAt: string;
  email: string;
  status: string;
};

async function initAmplitude(attrs: AmplitudeInitArgs) {
  if (!attrs.amplitudeApiKey || attrs.amplitudeApiKey === "") return;

  const uaPlugin = userAgentEnrichmentPlugin();

  await amplitude.init(attrs.amplitudeApiKey, attrs.amplitudeUserId, {
    minIdLength: 1,
    logLevel: isEnv(Environment.Production) ? 1 : 2,
    flushIntervalMillis: isEnv(Environment.Production) ? 10000 : 1000,
    cookieOptions: {
      secure: isEnv(Environment.Development) ? false : true, // Dev is http only so needs insecure cookies
      upgrade: true,
    },
    autocapture: {
      formInteractions: false,
      fileDownloads: true,
      pageViews: false,
      sessions: true,
      elementInteractions: {
        dataAttributePrefix: "analytics-action-",
        cssSelectorAllowlist: [
          'a',
          'button',
          'input',
          'select',
          'textarea',
          '[contenteditable="true" i]',
          '[data-amp-default-track]',
          '.amp-default-track',
          'label',
          'bc-card',
          'bc-card-panel',
          'bc-button',
          'bc-drawer',
          'bc-link',
          'bc-modal',
          'bc-modal-form',
          'bc-select',
          'bc-toggle',
          'bc-form',
          'bc-stepper',
          'bc-text-area',
          'bc-text-field',
          'bc-toggle',
        ]
      }
    },
    plan: { source: "web-admin" },
  }).promise;

  const identifyEvent = new amplitude.Identify();
  amplitude.identify(identifyEvent);

  await amplitude.add(uaPlugin).promise;

  if (!isEnv(Environment.Production)) console.log("[Amplitude] Browser Analytics SDK Initialized");

  return amplitude;
}

type PlanInterval = "Annual" | "Monthly";

function intervalFromParams(): PlanInterval {
  const frequency = new URLSearchParams(window.location.search).get(
    "frequency",
  );

  if (frequency === "annual") return "Annual";
  else return "Monthly";
}

const platinum_monthly_price = 15.0;
const platinum_annual_price = 144.0;
const diamond_monthly_price = 30.0;
const diamond_annual_price = 288.0;

function planFromSlug(slug: string) {
  const interval = intervalFromParams();

  let price = 0.0;

  if (slug === "gold") price = 0.0;
  else if (slug === "platinum" && interval === "Monthly")
    price = platinum_monthly_price;
  else if (slug === "platinum" && interval === "Annual")
    price = platinum_annual_price;
  else if (slug === "diamond" && interval === "Monthly")
    price = diamond_monthly_price;
  else if (slug === "diamond" && interval === "Annual")
    price = diamond_annual_price;

  const term = interval === "Annual" ? 12 : 1;

  return {
    Name: capitalize(slug),
    Type: slug === "gold" ? "Free" : "Paid",
    Price: price,
    Interval: interval,
    Term: term,
  };
}

function handleLocation(attrs: AmplitudeInitArgs) {
  routes.some((routeCallback) => {
    return routeCallback(attrs);
  }) ||
    untrackedRoutes.some((routeCallback) => {
      return routeCallback();
    }) ||
    track("Other Page Viewed");
}

function trackUpgradeClicks() {
  document.body.addEventListener("click", async (event) => {
    // Composed path will return all the elements that the click event passed
    // through including those in open shadow roots
    const target = event.composedPath()[0] as HTMLElement | null;
    if (!target) return;

    const elementWithUpgradeEvent: HTMLElement | null = target.closest("[data-upgradeeventname]");

    if (elementWithUpgradeEvent) {
      const upgradeEventName = elementWithUpgradeEvent.getAttribute("data-upgradeeventname");
      const upgradeProductArea = elementWithUpgradeEvent.getAttribute("data-upgradeproductarea");
      const upgradeProductSubArea = elementWithUpgradeEvent.getAttribute("data-upgradeproductsubarea");

      if (upgradeEventName) {
        const eventProps: Record<string, string> = {};

        if (upgradeProductArea) eventProps["Product Area"] = upgradeProductArea;
        if (upgradeProductSubArea) eventProps["Product Sub Area"] = upgradeProductSubArea;

        track(`Upgrade CTA [${upgradeEventName}] Clicked`, eventProps);

        eventProps["Feature Name"] = upgradeEventName;
        track("Any Upgrade CTA Clicked", eventProps);
      }
    }
  });
}

export function track(eventName: string, eventAttrs: Record<string, any> = {}) {
  if (eventName === "") return;
  let url = new URL(window.location.href);

  return amplitude?.track(eventName, {
    "Page Url": url.href,
    "Page Path": url.pathname,
    "Page Domain": url.host,
    "Page Title": document.title,
    "Page Query": window.location.search,
    "Page Hash": window.location.hash,
    "User Agent": navigator.userAgent,
    "User Agent Data": {
      Mobile: navigator.userAgentData?.mobile,
      Platform: navigator.userAgentData?.platform,
    },
    "Viewport Width": window.innerWidth,
    "Viewport Height": window.innerHeight,
    "Screen Width": window.screen.width,
    "Screen Height": window.screen.height,
    "Device Orientation": window.screen.orientation?.type || undefined,
    ...eventAttrs,
  });
}

/** return value is a function to call to clean up the location change listener */
export async function init(attrs: AmplitudeInitArgs): Promise<() => void> {
  // body is new for each turbo load so we need to reattach the event listener
  trackUpgradeClicks();

  if (!(window as any).amplitude) {
    (window as any).amplitude = await initAmplitude(attrs);

    handleLocation(attrs);
  }

  return onLocationChange(() => {
    const isTurboNav = document.body.classList.contains("turbo-loading");
    const callback = () => handleLocation(attrs);

    if (isTurboNav) {
      document.addEventListener("turbo:load", callback, { once: true });
    } else {
      requestAnimationFrame(callback);
    }
  });
}
